import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    InputAdornment,
    Skeleton,
    Stack,
    useMediaQuery
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Video } from '../../../../api/interfaces/Video';
import { getVideoBySubjectId } from '../../../../api/services/video';
import { handleApiError } from '../../../../utils/errorHandler';
import { pushNotification } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import MarkAsWatchedButton from '../../../../components/common/MarkAsWatchedButton/MarkAsWatchedButton';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import getTheme from '../../../../theme/theme';
import { PinnacleThemes } from '../../../../theme/types';
import { VideoGalleryItemProps } from './VideoGalleryItemProps.types';
import PinnacleSearchField from '../../../../components/PinnacleSearchField/PinnacleSearchField';
import { ClickableCard, ClickableCardBase, ImageClipper, ImageContainer } from '../VideoListItem/VideoListItem.styled';
import PinnacleVideoThumbnail from '../../../../components/PinnacleVideoThumbnail/PinnacleVideoThumbnail';
import { isVideoPlayable } from '../../../../utils/videoPlayable';
import { E_CANNOT_MARK_AS_WATCHED } from '../../../../utils/constants';
import { profile } from '../../../../api/services/user';
import { useNavigate } from 'react-router-dom';
import { watchVideoRedirect } from '../../../../utils/watchVideoRedirect';

const VideoGalleryItem: React.FC<VideoGalleryItemProps> = ({
    subjectId,
    title,
    onTrialStatusCheck
}) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const [allVideos, setAllVideos] = useState<Video[]>([]);
    const [displayVideos, setDisplayVideos] = useState<Video[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const [currentSortBy, setCurrentSortBy] = useState<'createdAt' | 'title' | ''>('');
    const [currentOrderBy, setCurrentOrderBy] = useState<'ASC' | 'DESC'>('ASC');
    const [isLoading, setIsLoading] = useState(false);
    const [isComputingPercentage, setIsComputingPercentage] = useState(false);
    const [percentageWatched, setPercentageWatched] = useState(0);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searching, setSearching] = useState(false);
    const [lastSearchTerm, setLastSearchTerm] = useState<string>(searchTerm);
    const [lastSortBy, setLastSortBy] = useState<string>(currentSortBy);
    const [lastOrderBy, setLastOrderBy] = useState<string>(currentOrderBy);
    const [skipNextEffect, setSkipNextEffect] = useState(false);
    const [isAccountTrial, setIsAccountTrial] = useState<boolean | null>(null);

    useEffect(() => {
        const fetchVideos = async () => {
            setIsLoading(true);
            try {
                const profileResponse = await profile();
                const userProfile = profileResponse.data.user;
                if (!isAccountTrial || isAccountTrial !== userProfile.isTrial) {
                    // Sync the account trial status
                    setIsAccountTrial(userProfile.isTrial ?? false);
                }
                const response = await getVideoBySubjectId(subjectId as string);
                setAllVideos(response.data);
                setDisplayVideos(response.data);
                computePercentage(response.data);
            } catch (err) {
                const { errorMessage } = handleApiError(err, { redirectOnAuthError: true });
                pushNotification({ details: errorMessage, type: NotificationTypes.Error });
            } finally {
                setIsLoading(false);
            }
        };

        if (subjectId) fetchVideos();
    }, [subjectId, isAccountTrial]);

    useEffect(() => {
        if (!allVideos.length) return;

        let filtered = [...allVideos];
        let shouldShowSkeleton = false;

        if (debouncedSearchTerm !== lastSearchTerm) {
            shouldShowSkeleton = true;
            setLastSearchTerm(debouncedSearchTerm);
        }

        if (currentSortBy !== lastSortBy || currentOrderBy !== lastOrderBy) {
            shouldShowSkeleton = true;
            setLastSortBy(currentSortBy);
            setLastOrderBy(currentOrderBy);
        }

        if (shouldShowSkeleton) {
            setSearching(true);
        }

        if (debouncedSearchTerm) {
            const lower = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(v => v.title?.toLowerCase().includes(lower));
        }

        if (currentSortBy) {
            filtered.sort((a, b) => {
                const aVal = (a[currentSortBy] ?? '') as string;
                const bVal = (b[currentSortBy] ?? '') as string;
                if (aVal < bVal) return currentOrderBy === 'ASC' ? -1 : 1;
                if (aVal > bVal) return currentOrderBy === 'ASC' ? 1 : -1;
                return 0;
            });
        }

        const timer = setTimeout(() => {
            setDisplayVideos(filtered);
            setSearching(false);
        }, 100);

        return () => clearTimeout(timer);

    }, [debouncedSearchTerm, allVideos, currentSortBy, currentOrderBy, lastOrderBy, lastSearchTerm, lastSortBy]);

    const computePercentage = (videos: Video[]) => {
        setIsComputingPercentage(true);
        const watched = videos.filter(v => v.isWatched).length;
        const percent = videos.length ? Math.round((watched / videos.length) * 100) : 0;
        setPercentageWatched(percent);
        setIsComputingPercentage(false);
    };

    const handleMarkAsWatched = (videoId: string, watched: boolean, playable: boolean) => {
        if (playable) {
            setSkipNextEffect(true);
            setAllVideos(prev => {
                const updated = prev.map(v =>
                    v._id === videoId ? { ...v, isWatched: watched } : v
                );
                computePercentage(updated);
                return updated;
            });

            setDisplayVideos(prev => {
                const updated = prev.map(v =>
                    v._id === videoId ? { ...v, isWatched: watched } : v
                );
                return updated;
            });
        } else {
            pushNotification({
                details: E_CANNOT_MARK_AS_WATCHED,
                type: NotificationTypes.Info,
            });
        }

    };

    const handleSortChange = (sortBy: 'createdAt' | 'title', orderBy: 'ASC' | 'DESC') => {
        setCurrentSortBy(sortBy);
        setCurrentOrderBy(orderBy);
        setAnchorEl(null);
    };

    return (
        <>
            {/* OVERALL WATCH PERCENTAGE */}
            {!isComputingPercentage ? ( 
                <Typography variant="h6" sx={{ fontSize: 14, mt: 1 }}>
                    <Box component="span" sx={{ color: 'var(--color-green-500)' }}>
                        {percentageWatched}%
                    </Box>{' '}
                    watched
                </Typography>
            ) : (
                <Skeleton variant="text" width={100} height={20} />
            )}

            {/* SORT + SEARCHFIELD */}
            <Stack
                direction={isMobile ? 'column-reverse' : 'row'}
                spacing={2}
                alignItems={isMobile ? 'left' : 'center'}
                justifyContent={isMobile ? 'left' : 'center'}
                sx={{
                    mt: 2,
                    width: '100%',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
            >
                <IconButton
                    size="large"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        color: Boolean(anchorEl) ? theme.palette.common.black : theme.palette.common.white,
                        backgroundColor: Boolean(anchorEl) ? 'secondary.main' : 'transparent',
                        justifyContent: isMobile ? 'left' : 'center',
                        alignItems: isMobile ? 'left' : 'center',
                        '&:hover': {
                            backgroundColor: Boolean(anchorEl) ? 'secondary.main' : 'rgba(255, 255, 255, 0.1)',
                        },
                        flexShrink: 0,
                    }}
                >
                    <SortOutlinedIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                    <Box sx={{ width: '100%', maxWidth: 400 }}>
                        <PinnacleSearchField
                            variant="outlined"
                            label="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: '100%' }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon sx={{ color: 'var(--color-gray-500)' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Stack>

            {/* SORT MENU */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => handleSortChange('createdAt', 'DESC')}>
                    Sort by Date
                </MenuItem>
                <MenuItem onClick={() => handleSortChange('title', 'ASC')}>
                    Sort by Name
                </MenuItem>
            </Menu>

            {/* VIDEO GRID */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                    pt: 3,
                    minHeight: { xs: 300, md: 400 },
                    minWidth: { xs: 0, md: 600 },
                    width: '100%',
                    maxWidth: { xs: '100vw', md: 836 },
                    mx: 'auto',
                    alignItems: displayVideos.length === 0 ? 'center' : undefined,
                    boxSizing: 'border-box',
                }}
            >
                {(isLoading || (searching && !skipNextEffect)) ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                        <Box key={idx} sx={{
                            width: {
                                xs: '100%',
                                sm: 'calc(50% - 16px)',
                                md: 'calc(25% - 16px)',
                            },
                            minWidth: {
                                md: '150px'
                            }
                        }}>
                            <Skeleton variant="rectangular" width="100%" height={isMobile ? 180 : 120} sx={{ borderRadius: 1 }} />
                            <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                            <Skeleton variant="text" width="60%" height={16} />
                        </Box>
                    ))
                ) : displayVideos.length > 0 ? (
                    displayVideos.map((video) => {
                        const playable = isVideoPlayable(isAccountTrial ?? false, video.isTrial ?? false, (video.videoUrl ?? '') || (video.videoUrl1 ?? '') || (video.videoUrl2 ?? ''));
                        return (
                            <Box
                                key={video._id}
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: 'calc(50% - 16px)',
                                        md: 'calc(25% - 16px)',
                                    },
                                    boxSizing: 'border-box',
                                }}
                            >
                                <ClickableCardBase as="div">
                                    <ClickableCard sx={{ margin: '0 !important' }} as="div">
                                        <ImageContainer playable={playable} hoverDisabled={isMobile ? true : false} onClick={() => watchVideoRedirect(navigate, video, playable)}>
                                            <ImageClipper>
                                                <PinnacleVideoThumbnail src={video.thumbnail} alt={video.title} zoomEnabled={false} />
                                            </ImageClipper>
                                            {!playable && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'rgba(0, 0, 0, 0.6)',
                                                        zIndex: 2,
                                                        pointerEvents: 'none',
                                                        borderRadius: 'inherit',
                                                    }}
                                                >
                                                    <LockOutlinedIcon
                                                        sx={{
                                                            fontSize: 45,
                                                            color: theme.palette.common.white,
                                                            borderRadius: '50%',
                                                            padding: 0.5,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </ImageContainer>
                                        <Box sx={{ pt: 1, textAlign: 'left' }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography
                                                    className="ellipsis"
                                                    variant="h6"
                                                    fontWeight={600}
                                                    sx={{ flex: 1, pr: 1 }}
                                                >
                                                    {video.title}
                                                </Typography>
                                                {playable && (
                                                    <MarkAsWatchedButton
                                                        videoId={video._id ?? ''}
                                                        isWatched={video.isWatched ?? false}
                                                        onChange={(watched) => handleMarkAsWatched(video._id ?? '', watched, playable)}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" sx={{ fontSize: 12 }}>
                                                {video.duration}
                                            </Typography>
                                        </Box>
                                    </ClickableCard>
                                </ClickableCardBase>
                            </Box>
                        );
                    })
                ) : (
                    <Typography variant="h5" sx={{ width: '100%', textAlign: 'center' }}>
                        No videos found.
                    </Typography>
                )}
            </Box>
        </>
    );
};

export default VideoGalleryItem;
